/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { StyledDatePickerButton } from '#PRIVATE_StyledDatePickerButton';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';
import { BundleType } from '#resources/nls/bundle';
import { getNumericFormattedYear } from '#utils/UNSAFE_calendarDateUtils';
import { ComponentProps } from 'preact/compat';
import { useCellNavigation } from '../UNSAFE_DatePicker/useCellNavigation';
import { useFocusableCell } from '../UNSAFE_DatePicker/useFocusableCell';

export type UseYearCellProps = {
  /**
   * Specifies if this year is today's year.
   */
  isCurrent: boolean;

  /**
   * Specifies if this year should be focused first when one
   * tabs into the YearsGridView.
   */
  isFocusable: boolean;

  /**
   * Specifies if this year is currently focused.
   */
  isFocused: boolean;

  /**
   * Specifies if this year is currently selected.
   */
  isSelected: boolean;

  /**
   * The year represented by this YearCell.
   */
  year: number;

  /**
   * A callback that will be invoked when a selection action is performed on this YearCell.
   * @param payload The payload containing the year represented by this cell.
   */
  onAction: (payload: { year: number }) => void;

  /**
   * A callback that will be invoked when a navigation action is performed on this YearCell.
   * @param payload The payload specifying the direction of the navigation performed and the year.
   */
  onNavigation: (payload: {
    direction: 'up' | 'next' | 'down' | 'previous' | 'previousDecade' | 'nextDecade';
    year: number;
  }) => void;
};

/**
 * A custom hook that adds functionalities to the YearCell component and computes properties for the
 * StyledDatePickerButton.
 */
export const useYearCell = ({
  isCurrent,
  isFocusable,
  isFocused,
  isSelected,
  year,
  onAction,
  onNavigation
}: UseYearCellProps) => {
  const { locale } = useUser();
  const yearText = getNumericFormattedYear(locale, year, 1);
  // custom aria label for selected and current year
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ariaLabel = `${yearText}${isCurrent ? `, ${translations.datePicker_currentDate()}` : ''}${
    isSelected ? `, ${translations.datePicker_selected()}` : ''
  }`;

  const buttonProps = useFocusableCell({ isFocusable, isFocused });
  const buttonEventHandlers = useCellNavigation({
    navigationMap: {
      ArrowDown: 'down',
      ArrowLeft: { ltr: 'previous', rtl: 'next' },
      ArrowRight: { ltr: 'next', rtl: 'previous' },
      ArrowUp: 'up',
      PageDown: 'previousDecade',
      PageUp: 'nextDecade'
    },
    payload: { year },
    onAction,
    onNavigation
  });

  return {
    buttonProps: {
      'aria-label': ariaLabel,
      isActivable: true,
      label: yearText,
      isHoverable: true,
      isSelected,
      isToday: isCurrent,
      variant: 'year' as const,
      ...buttonProps,
      ...buttonEventHandlers
    } as ComponentProps<typeof StyledDatePickerButton>
  };
};
